import * as v from 'valibot'
import { ApiError } from './client'
import type { Actress, SearchSuggestion, SessionUser, Tag, TagRef, VideoCard, VideoDetail } from './types'

// Mock fixtures are already view models. Real API responses use the canonical Go DTOs.
export const usesMockAPI =
  import.meta.env.MODE === 'mock' || (import.meta.env.DEV && import.meta.env.MODE !== 'integration')

const ref = v.object({ id: v.string(), name: v.string() })
const nullableString = v.nullable(v.string())
const nullableNumber = v.nullable(v.number())
const translation = v.object({ locale: v.string(), title: v.string() })
const card = v.object({
  id: v.string(),
  code: v.string(),
  title: v.string(),
  imageUrl: nullableString,
  releaseDate: nullableString,
  actresses: v.array(v.object({ actress: ref })),
  magnetCount: v.number(),
  hasTrailer: v.boolean(),
  isFavorited: v.boolean(),
  translations: v.array(translation),
})
const profile = v.object({
  id: v.string(),
  name: v.string(),
  imageUrl: nullableString,
  aliases: v.array(ref),
  birthDate: nullableString,
  debutYear: nullableNumber,
  height: nullableNumber,
  measurements: nullableString,
  isSubscribed: v.boolean(),
})
const actress = v.object({ ...profile.entries, videoCount: v.number() })
const magnet = v.object({
  id: v.string(),
  name: v.string(),
  link: v.string(),
  size: v.pipe(v.string(), v.regex(/^\d+$/)),
  seeders: nullableNumber,
  source: v.string(),
})
const detail = v.object({
  id: v.string(),
  code: v.string(),
  title: v.string(),
  imageUrl: nullableString,
  releaseDate: nullableString,
  duration: nullableNumber,
  isFavorited: v.boolean(),
  actresses: v.array(profile),
  tags: v.array(v.object({ tag: ref })),
  maker: v.nullable(ref),
  series: v.nullable(ref),
  translations: v.array(translation),
  magnets: v.array(magnet),
  trailerR2Url: nullableString,
})
const tag = v.object({
  ...ref.entries,
  category: v.picklist([
    'QUALITY_FORMAT',
    'RELEASE_LABEL',
    'BODY_PERSONA',
    'ROLE_COSTUME',
    'RELATION_CAST',
    'GENRE_SCENARIO',
    'PLAY_FETISH',
    'OTHER',
  ]),
  videoCount: v.number(),
  favoriteCount: v.number(),
})
const suggestion = v.variant('entityType', [
  v.object({
    entityType: v.literal('video'),
    id: v.string(),
    code: v.string(),
    title: v.string(),
    imageUrl: nullableString,
    translations: v.array(translation),
  }),
  v.object({ entityType: v.literal('actress'), ...ref.entries }),
])
const quota = v.object({ used: v.number(), limit: nullableNumber })
const session = v.object({
  id: v.string(),
  email: v.string(),
  role: v.picklist(['ADMIN', 'USER']),
  emailVerified: v.boolean(),
  membership: v.object({
    status: v.picklist(['FREE', 'ACTIVE', 'EXPIRED']),
    type: v.nullable(v.picklist(['MONTHLY', 'YEARLY', 'LIFETIME'])),
    endDate: nullableString,
  }),
  quota: v.object({ subscriptions: quota, onlineSearch: quota, magnetRefresh: quota }),
  telegram: v.object({ bound: v.boolean(), username: nullableString, rewardClaimed: v.boolean() }),
  onboarding: v.nullable(v.picklist(['PENDING', 'IN_PROGRESS', 'COMPLETED', 'SKIPPED'])),
})

function parse<S extends v.GenericSchema>(schema: S, input: unknown): v.InferOutput<S> {
  const result = v.safeParse(schema, input)
  if (!result.success) throw new ApiError(502, 'BAD_RESPONSE', '服务响应不符合接口合同')
  return result.output
}
function titleFor(title: string, translations: v.InferOutput<typeof translation>[], locale: string) {
  return translations.find((entry) => entry.locale === locale)?.title || title
}
export function adaptVideoCard(input: unknown, locale: string): VideoCard {
  const data = parse(card, input)
  return {
    id: data.id,
    code: data.code,
    title: titleFor(data.title, data.translations, locale),
    titleOriginal: data.title,
    releaseDate: data.releaseDate,
    imageUrl: data.imageUrl,
    actresses: data.actresses.map((entry) => entry.actress),
    magnetCount: data.magnetCount,
    hasTrailer: data.hasTrailer,
    favorited: data.isFavorited,
    isNew: null,
  }
}
function profileView(data: v.InferOutput<typeof profile>, videoCount: number | null): Actress {
  return {
    id: data.id,
    name: data.name,
    imageUrl: data.imageUrl,
    aliases: data.aliases.map((entry) => entry.name),
    birthDate: data.birthDate,
    debutYear: data.debutYear,
    height: data.height,
    measurements: data.measurements,
    subscribed: data.isSubscribed,
    videoCount,
    latestCode: null,
  }
}
export function adaptActress(input: unknown): Actress {
  const data = parse(actress, input)
  return profileView(data, data.videoCount)
}
export function adaptSubscription(input: unknown): Actress & { newCount: number | null } {
  const data = parse(
    v.object({
      actress: v.object({ ...actress.entries, latestCode: nullableString }),
      newCount: nullableNumber,
    }),
    input,
  )
  return {
    ...profileView(data.actress, data.actress.videoCount),
    latestCode: data.actress.latestCode,
    newCount: data.newCount,
  }
}
export function adaptOnboarding(input: unknown) {
  return parse(
    v.object({
      status: v.picklist(['PENDING', 'IN_PROGRESS', 'COMPLETED', 'SKIPPED']),
      selectedActressIds: v.array(v.string()),
      candidates: v.array(v.object({ ...ref.entries, imageUrl: nullableString, videoCount: v.number() })),
    }),
    input,
  )
}
export function adaptVideoDetail(input: unknown, locale: string): VideoDetail {
  const data = parse(detail, input)
  return {
    id: data.id,
    code: data.code,
    title: titleFor(data.title, data.translations, locale),
    titleOriginal: data.title,
    releaseDate: data.releaseDate,
    imageUrl: data.imageUrl,
    actresses: data.actresses.map(({ id, name }) => ({ id, name })),
    magnetCount: data.magnets.length,
    hasTrailer: !!data.trailerR2Url,
    favorited: data.isFavorited,
    isNew: null,
    duration: data.duration,
    maker: data.maker?.name ?? null,
    series: data.series?.name ?? null,
    tags: data.tags.map((entry) => entry.tag),
    magnets: data.magnets,
    bestMagnetId: null,
    trailerUrl: data.trailerR2Url,
    actressesDetail: data.actresses.map((entry) => profileView(entry, null)),
  }
}
export function adaptTag(input: unknown): Tag {
  return parse(tag, input)
}
export function adaptTagRef(input: unknown): TagRef {
  return parse(ref, input)
}
export function adaptSuggestion(input: unknown, locale: string): SearchSuggestion {
  const data = parse(suggestion, input)
  return data.entityType === 'video'
    ? {
        kind: 'video',
        id: data.id,
        label: data.code,
        sub: titleFor(data.title, data.translations, locale),
        imageUrl: data.imageUrl,
      }
    : { kind: 'actress', id: data.id, label: data.name, sub: '' }
}
export function adaptSession(input: unknown, mock = usesMockAPI): SessionUser {
  return mock ? parse(session, input) : parse(v.object({ session }), input).session
}
export function mapItems<T>(input: unknown, adapter: (item: unknown) => T): T[] {
  if (!Array.isArray(input)) throw new ApiError(502, 'BAD_RESPONSE', '服务响应不符合接口合同')
  return input.map(adapter)
}
